import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, apiFormData } from '@/lib/api';
import { courseMissingCategoryToast, courseSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { courseBackUrl, RETURN_TO_CATEGORIES } from '@/lib/course-admin-navigation';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { CourseFormModern, type CourseFormValues } from './CourseFormModern';
import type { CourseFormTabId } from './course-form-constants';
import { fieldTabForElement } from './course-form-constants';
import { buildCourseFormData, flattenCategories } from './course-form-utils';

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

let missingCategoryToastAt = 0;

function notifyMissingCategory(toast: (item: ReturnType<typeof courseMissingCategoryToast>) => void) {
  const now = Date.now();
  if (now - missingCategoryToastAt < 1500) return;
  missingCategoryToastAt = now;
  toast(courseMissingCategoryToast());
}

export function CourseFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);

  const [activeTab, setActiveTab] = useState<CourseFormTabId>('general');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [coverLabel, setCoverLabel] = useState('Subir miniatura');
  const [bannerLabel, setBannerLabel] = useState('Click para añadir banner');

  const backTo = courseBackUrl(params);
  const backLabel =
    params.get('return_to') === RETURN_TO_CATEGORIES ? 'Volver a categorías' : 'Volver a cursos';

  const { hasPermission } = usePermissions();

  const { data: contractTemplatesData } = useQuery({
    queryKey: ['contracts', 'templates'],
    queryFn: () => api<{ data: { id: string; title: string }[] }>('/contracts/templates'),
    enabled: hasPermission(PERMISSIONS.CONTRATOS_PLANTILLAS),
  });

  const { data: categories, isSuccess: categoriesLoaded } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<CategoryNode[]>('/categories'),
  });

  const { data: formOptions } = useQuery({
    queryKey: ['courses', 'form-options'],
    queryFn: () =>
      api<{
        instructors: { id: string; fullName: string }[];
        supervisors: { id: string; fullName: string }[];
        roles: { id: string; label: string; userCount: number }[];
      }>(
        '/courses/form-options',
      ),
  });

  const { register, handleSubmit, watch, setValue } = useForm<CourseFormValues>({
    defaultValues: {
      name: '',
      code: '',
      slug: '',
      status: 'draft',
      shortDescription: '',
      fullDescription: '',
      categoryId: params.get('category_id') ?? '',
      visibility: 'public',
      primaryInstructorId: user?.id ?? '',
      additionalInstructors: [],
      supervisorUserIds: [],
      instructorRoleIds: [],
      isFree: true,
      price: '',
      requiresEnrollment: true,
      allowComments: true,
      showProgress: true,
      minCompliancePercent: '',
      academicHoursTime: '',
      startDate: '',
      endDate: '',
      maxEnrollment: '',
      metaTitle: '',
      metaDescription: '',
      contractTemplateId: '',
      requiresPrerequisiteCourse: false,
      prerequisiteCourseId: '',
      prerequisiteAssessmentId: '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: CourseFormValues) => {
      const course = await apiFormData<{ id: string }>('/courses', buildCourseFormData(values, coverFile, bannerFile), {
        method: 'POST',
      });
      if (hasPermission(PERMISSIONS.CURSOS_EDITAR)) {
        await api(`/courses/${course.id}/supervisors`, { method: 'PUT', body: { userIds: values.supervisorUserIds } });
      }
      return course;
    },
    onSuccess: (course, values) => {
      void queryClient.invalidateQueries({ queryKey: ['courses'] });
      if (values.categoryId) {
        void queryClient.invalidateQueries({ queryKey: ['courses', 'category', values.categoryId] });
      }
      toast(courseSuccessToast('create'));
      if (params.get('return_to') === RETURN_TO_CATEGORIES && values.categoryId) {
        navigate(courseBackUrl(params, values.categoryId));
        return;
      }
      navigate(`/courses/${course.id}`);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : '';
      if (/curso previo|evaluación/i.test(message)) setActiveTab('requirements');
      toast(roleActionErrorToast(error, 'No se pudo guardar el curso'));
    },
  });

  const categoryOptions = flattenCategories(categories ?? []);
  const instructors = formOptions?.instructors ?? [];
  const hasCategories = categoryOptions.length > 0;

  useEffect(() => {
    if (!categoriesLoaded || hasCategories) return;
    notifyMissingCategory(toast);
  }, [categoriesLoaded, hasCategories, toast]);

  function handleInvalid(event: FormEvent<HTMLFormElement>) {
    if (!hasCategories) {
      event.preventDefault();
      notifyMissingCategory(toast);
      return;
    }
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('input, select, textarea')) return;
    event.preventDefault();
    setActiveTab(fieldTabForElement(target));
    window.requestAnimationFrame(() => {
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      ) {
        target.focus();
        target.reportValidity();
      }
    });
  }

  return (
    <div className="lms-course-form lms-site-admin-hub-page font-sans">
      <div className="lms-course-form__shell">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <HubBackLink to={backTo} label={backLabel} />
            <div>
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Nuevo curso</h1>
              <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
                Crear un curso en el catálogo académico
              </p>
            </div>
          </div>
        </div>

        <form
          ref={formRef}
          onSubmit={(event) => {
            if (!hasCategories) {
              event.preventDefault();
              notifyMissingCategory(toast);
              return;
            }
            void handleSubmit((values) => mutation.mutate(values))(event);
          }}
          onInvalidCapture={handleInvalid}
          className="lms-course-form__layout"
        >
          <CourseFormModern
            register={register}
            watch={watch}
            setValue={setValue}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            categoryOptions={categoryOptions}
            instructors={instructors}
            supervisors={formOptions?.supervisors ?? []}
            roles={formOptions?.roles ?? []}
            contractTemplates={contractTemplatesData?.data ?? []}
            coverLabel={coverLabel}
            bannerLabel={bannerLabel}
            saveError={mutation.isError && mutation.error instanceof Error ? mutation.error.message : undefined}
            onCoverChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              setCoverFile(file);
              setCoverLabel(file ? file.name : 'Subir miniatura');
            }}
            onBannerChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              setBannerFile(file);
              setBannerLabel(file ? file.name : 'Click para añadir banner');
            }}
          />

          <footer className="lms-course-form__footer">
            <Link to={backTo} className="lms-course-form__btn lms-course-form__btn--cancel">
              Cancelar
            </Link>
            <button type="submit" className="lms-course-form__btn lms-course-form__btn--submit" disabled={mutation.isPending}>
              Crear curso
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
